import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TemplateCard = ({ 
  template, 
  onEdit, 
  onDuplicate, 
  onDelete, 
  onUse,
  className = '' 
}) => {
  const [showActions, setShowActions] = useState(false);

  const getCategoryColor = (category) => {
    switch (category) {
      case 'birthday':
        return 'bg-accent text-accent-foreground';
      case 'follow-up':
        return 'bg-primary text-primary-foreground';
      case 'holiday':
        return 'bg-success text-success-foreground';
      case 'custom':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div 
      className={`bg-surface border border-border rounded-lg p-4 hover:shadow-card transition-micro hover-lift ${className}`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-text-primary truncate mb-1">
            {template?.name}
          </h3>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(template?.category)}`}>
              {template?.category}
            </span>
            {template?.variables && template?.variables?.length > 0 && (
              <span className="text-xs text-text-secondary bg-muted px-2 py-1 rounded-full">
                {template?.variables?.length} variables
              </span>
            )}
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className={`flex items-center space-x-1 transition-opacity ${showActions ? 'opacity-100' : 'opacity-0 lg:opacity-100'}`}>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onUse(template)}
            className="h-8 w-8"
            title="Use Template"
          >
            <Icon name="Send" size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(template)}
            className="h-8 w-8"
            title="Edit Template"
          >
            <Icon name="Edit" size={16} />
          </Button>
        </div>
      </div>
      {/* Preview Text */}
      <div className="mb-4">
        <p className="text-text-secondary text-sm line-clamp-3 leading-relaxed">
          {template?.content}
        </p>
      </div>
      {/* Stats and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4 text-xs text-text-secondary">
          <div className="flex items-center space-x-1">
            <Icon name="BarChart3" size={14} />
            <span>{template?.usageCount || 0} uses</span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Calendar" size={14} />
            <span>{formatDate(template?.lastModified)}</span>
          </div>
        </div>

        {/* Mobile Actions Menu */}
        <div className="lg:hidden">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowActions(!showActions)}
            className="h-8 w-8"
          >
            <Icon name="MoreVertical" size={16} />
          </Button>
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center space-x-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDuplicate(template)}
            iconName="Copy"
            iconPosition="left"
          >
            Duplicate
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(template)}
            iconName="Trash2"
            iconPosition="left"
            className="text-error hover:text-error"
          >
            Delete
          </Button>
        </div>
      </div>
      {/* Mobile Actions Panel */}
      {showActions && (
        <div className="lg:hidden mt-3 pt-3 border-t border-border flex items-center justify-end space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDuplicate(template)}
            iconName="Copy"
            iconPosition="left"
          >
            Duplicate
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(template)}
            iconName="Trash2"
            iconPosition="left"
            className="text-error border-error hover:bg-error hover:text-error-foreground"
          >
            Delete
          </Button>
        </div>
      )}
    </div>
  );
};

export default TemplateCard;